import { ReactNode } from 'react';
import { ColumnDefinition, Datatable, FormatDate, Title } from '@wyz/ui';
import { serialize } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useApi, useConfig, useLocale, Trans } from '~/common/context';
import { ConnectQueryKeys } from '~/connect/api';
import { IConnectionLog, IConnectionLogList } from '~/connect/types';

type IConnectionLogRow = IConnectionLog & { id: string };

export function ConnectionLogsTable({
  userId,
  showTitle = true,
  topSlot,
}: {
  userId: string;
  showTitle?: boolean;
  topSlot?: ReactNode;
}) {
  const { locale } = useLocale();
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const columns: Array<ColumnDefinition<IConnectionLogRow>> = [
    {
      field: 'loggedAt',
      sortable: false,
      width: '100%',
      headerName: <Trans i18nKey='app.logs.connection.date' />,
      renderCell: (parameters) => (
        <div>
          {!isNil(parameters.row.loggedAt) && (
            <FormatDate
              date={new Date(parameters.row.loggedAt)}
              locale={locale}
            />
          )}
        </div>
      ),
    },
    {
      field: 'ipAddress',
      sortable: false,
      width: '100%',
      headerName: <Trans i18nKey='app.logs.connection.ip' />,
      renderCell: (parameters) => <div>{parameters.row.ipAddress}</div>,
    },
  ];

  return (
    <Datatable.Container<IConnectionLogRow>
      defaults={{ limit: 10 }}
      columns={columns}
      fetcher={async (query) => {
        const formattedQuery = {
          limit: query.limit,
          page: query.page,
          userId,
        };
        const response = await callCoreApi<IConnectionLogList>(
          `/tenants/api/wtb/tenants/${tenantCode}/connection-logs?${serialize(
            formattedQuery,
          )}`,
          {},
        );

        return {
          ...response,
          data: response.data.map((row, index) => ({
            ...row,
            id: `${query.page}-${index}`,
          })),
        };
      }}
      queryKey={ConnectQueryKeys.connectionLogs.paginate(userId)}
    >
      <>
        {showTitle && (
          <div>
            <Title heading='page-title' weight='bold' className='mt-4'>
              <Trans i18nKey='app.logs.history' />
            </Title>
          </div>
        )}
        <Datatable.Background>
          <Datatable.Header />
          {topSlot}
          <Datatable.Table />
          <hr />
          <Datatable.Footer />
        </Datatable.Background>
      </>
    </Datatable.Container>
  );
}
